import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api.ts';
import {
    Search,
    Filter,
    MoreVertical,
    Edit,
    Trash2,
    Shield,
    Crown,
    User as UserIcon,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    X,
    Check,
    AlertTriangle
} from 'lucide-react';

interface User {
    _id: string;
    username: string;
    email?: string;
    role: string;
    avatar?: string;
    createdAt: string;
    subscription?: {
        tier: string;
        status: string;
    };
    usage?: {
        messages: number;
        images: number;
    };
    accountDeletedAt?: string;
}

interface Pagination {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

const AdminUsers: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchUsers = async (page = 1) => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '20',
                ...(search && { search }),
                ...(roleFilter && { role: roleFilter })
            });

            const response = await fetch(`${API_URL}/admin/users?${params}`, {
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                setUsers(data.users);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [search, roleFilter]);

    const handleUpdateRole = async (userId: string, newRole: string) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/users/${userId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ role: newRole })
            });
            const data = await response.json();

            if (data.success) {
                fetchUsers(pagination.page);
                setShowEditModal(false);
            }
        } catch (error) {
            console.error('Error updating user:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async (userId: string) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${API_URL}/admin/users/${userId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            const data = await response.json();

            if (data.success) {
                fetchUsers(pagination.page);
                setShowDeleteModal(false);
                setSelectedUser(null);
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        } finally {
            setActionLoading(false);
        }
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
                return <Crown size={16} style={{ color: '#ffd93d' }} />;
            default:
                return <UserIcon size={16} style={{ color: '#4ecdc4' }} />;
        }
    };

    const getTierColor = (tier?: string) => {
        switch (tier?.toLowerCase()) {
            case 'gold':
                return '#ffd93d';
            case 'silver':
                return '#c0c0c0';
            case 'bronze':
                return '#cd7f32';
            default:
                return '#4ecdc4';
        }
    };

    return (
        <div>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>
                    Gestión de Usuarios
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {pagination.total} usuarios registrados
                </p>
            </div>

            {/* Filters */}
            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    flex: 1,
                    minWidth: '250px',
                    position: 'relative'
                }}>
                    <Search size={18} style={{
                        position: 'absolute',
                        left: '14px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'rgba(255,255,255,0.4)'
                    }} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre o email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 12px 12px 44px',
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            outline: 'none'
                        }}
                    />
                </div>

                <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '12px',
                        color: 'white',
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value="">Todos los roles</option>
                    <option value="user">Usuario</option>
                    <option value="admin">Admin</option>
                </select>

                <button
                    onClick={() => fetchUsers(pagination.page)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(78, 205, 196, 0.2)',
                        border: '1px solid rgba(78, 205, 196, 0.3)',
                        borderRadius: '12px',
                        color: '#4ecdc4',
                        cursor: 'pointer'
                    }}
                >
                    <RefreshCw size={18} />
                </button>
            </div>

            {/* Users Table */}
            <div style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Usuario</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Rol</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Suscripción</th>
                            <th style={{ padding: '16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Registrado</th>
                            <th style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                                </td>
                            </tr>
                        ) : users.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                                    No se encontraron usuarios
                                </td>
                            </tr>
                        ) : (
                            users.map((user) => (
                                <tr key={user._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                    <td style={{ padding: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <img
                                                src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.username}`}
                                                alt={user.username}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '12px',
                                                    objectFit: 'cover'
                                                }}
                                                referrerPolicy="no-referrer"
                                            />
                                            <div>
                                                <p style={{ color: 'white', fontWeight: '600' }}>{user.username}</p>
                                                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>{user.email || 'Sin email'}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '6px 12px',
                                            background: user.role === 'admin' ? 'rgba(255, 217, 61, 0.2)' : 'rgba(78, 205, 196, 0.2)',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            color: user.role === 'admin' ? '#ffd93d' : '#4ecdc4'
                                        }}>
                                            {getRoleIcon(user.role)}
                                            {user.role}
                                        </span>
                                    </td>
                                    <td style={{ padding: '16px' }}>
                                        <span style={{
                                            padding: '6px 12px',
                                            background: `${getTierColor(user.subscription?.tier)}22`,
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            color: getTierColor(user.subscription?.tier)
                                        }}>
                                            {user.subscription?.tier || 'Free'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '16px', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                                        {new Date(user.createdAt).toLocaleDateString()}
                                    </td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                            <button
                                                onClick={() => { setSelectedUser(user); setShowEditModal(true); }}
                                                style={{
                                                    padding: '8px',
                                                    background: 'rgba(78, 205, 196, 0.2)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    color: '#4ecdc4',
                                                    cursor: 'pointer'
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => { setSelectedUser(user); setShowDeleteModal(true); }}
                                                style={{
                                                    padding: '8px',
                                                    background: 'rgba(255, 107, 107, 0.2)',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    color: '#ff6b6b',
                                                    cursor: 'pointer'
                                                }}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <button
                            onClick={() => fetchUsers(pagination.page - 1)}
                            disabled={pagination.page === 1}
                            style={{
                                padding: '8px 12px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === 1 ? 0.5 : 1
                            }}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                            Página {pagination.page} de {pagination.pages}
                        </span>
                        <button
                            onClick={() => fetchUsers(pagination.page + 1)}
                            disabled={pagination.page === pagination.pages}
                            style={{
                                padding: '8px 12px',
                                background: 'rgba(255,255,255,0.1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer',
                                opacity: pagination.page === pagination.pages ? 0.5 : 1
                            }}
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && selectedUser && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        width: '400px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                            <h2 style={{ color: 'white', fontSize: '20px' }}>Editar Usuario</h2>
                            <button
                                onClick={() => setShowEditModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>Usuario</p>
                            <p style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>{selectedUser.username}</p>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>Cambiar Rol</p>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => handleUpdateRole(selectedUser._id, 'user')}
                                    disabled={actionLoading}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: selectedUser.role === 'user' ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255,255,255,0.1)',
                                        border: selectedUser.role === 'user' ? '2px solid #4ecdc4' : '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <UserIcon size={18} />
                                    Usuario
                                </button>
                                <button
                                    onClick={() => handleUpdateRole(selectedUser._id, 'admin')}
                                    disabled={actionLoading}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: selectedUser.role === 'admin' ? 'rgba(255, 217, 61, 0.3)' : 'rgba(255,255,255,0.1)',
                                        border: selectedUser.role === 'admin' ? '2px solid #ffd93d' : '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '12px',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <Crown size={18} />
                                    Admin
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && selectedUser && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        padding: '32px',
                        width: '400px',
                        border: '1px solid rgba(255, 107, 107, 0.3)',
                        textAlign: 'center'
                    }}>
                        <AlertTriangle size={48} style={{ color: '#ff6b6b', marginBottom: '16px' }} />
                        <h2 style={{ color: 'white', fontSize: '20px', marginBottom: '8px' }}>¿Eliminar Usuario?</h2>
                        <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>
                            Esta acción eliminará permanentemente a <strong>{selectedUser.username}</strong> y todos sus datos.
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteUser(selectedUser._id)}
                                disabled={actionLoading}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)',
                                    border: 'none',
                                    borderRadius: '12px',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                {actionLoading ? 'Eliminando...' : 'Eliminar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AdminUsers;
